import { useMemo } from 'react';
import { isEqual, isNull, merge, set } from 'lodash';

import { trims } from 'utils';

export const INITIAL_VALUE = {
  id: 0,
  name: '',
  address: '',
  sendPauseMessage: false,
  sendPhoneVerification: false,
  useVisitorDoc: false,
  useTransportDoc: false,
  phone: '',
  aadRoles: [
    { id: 0, roleId: 4, title: 'ARM_VYPISKA', objectId: '' },
    { id: 0, roleId: 3, title: 'ARM_OHRANA', objectId: '' },
    { id: 0, roleId: 6, title: 'ARM_KIOSK', objectId: '' },
    { id: 0, roleId: 2, title: 'ARM_DIRECTOR', objectId: '' },
    { id: 0, roleId: 7, title: 'ARM_TVPANEL', objectId: '' },
    { id: 0, roleId: 1, title: 'ARM_MANAGER', objectId: '' },
  ],
  locations: [
    {
      id: 0,
      name: '',
      isDeleted: false,
      configurations: [
        {
          id: 0,
          name: '',
          isDeleted: false,
        },
      ],
    },
  ],
  driverSMS: [
    {
      driverSMSrule: false,
    },
  ],
  clientSMS: [
    {
      clientSMSrule: false,
    },
  ],
  ordering: {
    peripheralRoles: [
      {
        id: 0,
        peripheralHardwareId: 2,
        peripheralUrl: '',
        processName: 'Scan',
        allowedExitTolerance: 0,
      },
      {
        id: 0,
        peripheralHardwareId: 4,
        peripheralUrl: '',
        processName: 'Add User',
        allowedExitTolerance: 0,
      },
      {
        id: 0,
        peripheralHardwareId: 4,
        peripheralUrl: '',
        processName: 'Auth User',
        allowedExitTolerance: 0,
      },
    ],
  },
  peripheralHardwares: [
    {
      id: 1,
      model: 'Zebra GK420',
      name: 'Принтер етикеток',
      protocol: 'ZPLII',
      type: 'LabelPrinter',
    },
    {
      id: 2,
      model: 'Honeywell 4850DR',
      name: 'Фотосканер документів',
      protocol: 'jpeg',
      type: 'ImageScanner',
    },
    {
      id: 3,
      model: 'PlatformOne',
      name: 'Вантажні ваги',
      protocol: 'SMCAPI',
      type: 'Scales',
    },
    {
      id: 4,
      model: 'Suprema F2',
      name: 'Модуль FaceID',
      protocol: 'Suprema',
      type: 'FaceID',
    },
  ],
  securityGates: [
    {
      id: 0,
      name: '',
      isDeleted: false,
      peripheralRoles: [
        {
          id: 0,
          peripheralHardwareId: 2,
          peripheralUrl: '',
          processName: 'Scan',
          allowedExitTolerance: 0,
        },
        {
          id: 0,
          peripheralHardwareId: 3,
          peripheralUrl: '',
          processName: 'Entrance',
          allowedExitTolerance: 0,
          isManualGetWeight: false,
        },
        {
          id: 0,
          peripheralHardwareId: 3,
          peripheralUrl: '',
          processName: 'Exit',
          allowedExitTolerance: 0,
          isManualGetWeight: false,
        },
        {
          id: 0,
          peripheralHardwareId: 4,
          peripheralUrl: '',
          processName: 'Auth User',
          allowedExitTolerance: 0,
        },
      ],
    },
  ],
  useFaceScan: false,
  smsServiceSetting: {
    isWork: false,
    status: '',
  },
  screenRenderSettings: {
    id: 0,
    frequency: 30,
    logoStartTime: '0001-01-01T07:00:00',
    logoEndTime: '0001-01-01T21:00:00',
    imageUrl: null,
    imageFile: null,
    customScreens: [],
    isShowLogo: false,
    isShowCustomScreens: false,
  },
} as {
  [key: string]: any;
};

export const fillNullable = (data: any, fields: string[]) => {
  fields.forEach((field) => {
    if (!data[field]) {
      // eslint-disable-next-line
      data[field] = INITIAL_VALUE[field];
    }

    if (isEqual(field, 'securityGates')) {
      data[field].forEach((f: any, index: number) => {
        if (!f.peripheralRoles?.length || isNull(f.peripheralRoles)) {
          return set(data, `securityGates[${index}].peripheralRoles`, INITIAL_VALUE.securityGates[0].peripheralRoles);
        }

        const peripheralRoles: any[] = [];

        INITIAL_VALUE.securityGates[0].peripheralRoles.map((initRole: any) => {
          if (initRole.peripheralHardwareId === 3 && initRole.processName === 'Entrance') {
            const findEntrance = f.peripheralRoles.find(
              (b: any) =>
                b.peripheralHardwareId === initRole.peripheralHardwareId && trims(b.processName) === 'Entrance',
            );

            return findEntrance ? peripheralRoles.push(findEntrance) : peripheralRoles.push(initRole);
          }

          if (initRole.peripheralHardwareId === 3 && initRole.processName === 'Exit') {
            const findExit = f.peripheralRoles.find(
              (r: any) => r.peripheralHardwareId === initRole.peripheralHardwareId && trims(r.processName) === 'Exit',
            );

            return findExit ? peripheralRoles.push(findExit) : peripheralRoles.push(initRole);
          }

          const find = f.peripheralRoles.find((br: any) => br.peripheralHardwareId === initRole.peripheralHardwareId);
          return find ? peripheralRoles.push(find) : peripheralRoles.push(initRole);
        });

        set(data, `securityGates[${index}].peripheralRoles`, peripheralRoles);
      });
    }

    if (isEqual(field, 'ordering')) {
      if (!data[field].peripheralRoles?.length || isNull(data[field].peripheralRoles)) {
        return set(data, `ordering.peripheralRoles`, INITIAL_VALUE.ordering.peripheralRoles);
      }

      const peripheralRoles: any[] = [];

      INITIAL_VALUE.ordering.peripheralRoles.map((initRole: any) => {
        if (initRole.peripheralHardwareId === 4 && initRole.processName === 'Auth User') {
          const findEntrance = data[field].peripheralRoles.find(
            (f: any) =>
              f.peripheralHardwareId === initRole.peripheralHardwareId && trims(f.processName) === 'Auth User',
          );

          return findEntrance ? peripheralRoles.push(findEntrance) : peripheralRoles.push(initRole);
        }

        if (initRole.peripheralHardwareId === 4 && initRole.processName === 'Add User') {
          const findExit = data[field].peripheralRoles.find(
            (fa: any) =>
              fa.peripheralHardwareId === initRole.peripheralHardwareId && trims(fa.processName) === 'Add User',
          );

          return findExit ? peripheralRoles.push(findExit) : peripheralRoles.push(initRole);
        }

        const find = data[field].peripheralRoles.find(
          (fad: any) => fad.peripheralHardwareId === initRole.peripheralHardwareId,
        );
        return find ? peripheralRoles.push(find) : peripheralRoles.push(initRole);
      });
    }
    return data;
  });
};

export const usePrepareBranch = (id: number, values: any) =>
  useMemo(() => {
    const data = merge({}, INITIAL_VALUE, values);

    fillNullable(data, ['aadRoles', 'securityGates', 'locations', 'ordering', 'screenRenderSettings']);

    return data;
  }, [id, values]);
